import { G } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { ListContextProvider, TextField, useList, useRecordContext } from 'react-admin';

import { GetB2bCampaignOneResponse } from '@Common/types/bo/b2b/campaigns';

import { DatagridTyped, EmptyScreen } from '@sh4res/bo-common/components';
import { SelectChipField } from '@sh4res/bo-common/fields';
import { DocumentListViewerLink } from '@sh4res/bo-common/fields/DocumentListViewerLink';
import { EmptyFolderSvg } from '@sh4res/bo-common/graphics';
import { ColumnList, makeChoicesCommon } from '@sh4res/bo-common/utils';

const testId = 'BoB2bCampaignDocumentsTab';

type CampaignSourceDocumentType = 'affiliates_data' | 'allocations';

type CampaignSourceDocument = {
  id: CampaignSourceDocumentType;
  type: CampaignSourceDocumentType;
  document_name: string;
  document_url: string;
};

const campaignSourceDocumentTypeChoices = makeChoicesCommon<CampaignSourceDocumentType>()({
  affiliates_data: 'resources.bo_common.b2b.campaigns.tabs.documents.fields.affiliates_data',
  allocations: 'resources.bo_common.b2b.campaigns.tabs.documents.fields.allocations',
});

const getFileTitleFromUrl = (url: string): string =>
  decodeURIComponent(url.split('/').pop()?.split('?')[0] || '');

const columns = {
  id: (
    // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- synthetic local rows, no show page
    <TextField
      label="resources.bo_common.b2b.documents.fields.id"
      data-testid={`${testId}_IdField`}
    />
  ),
  type: (
    <SelectChipField
      choices={campaignSourceDocumentTypeChoices}
      label="resources.bo_common.b2b.documents.fields.type"
      data-testid={`${testId}_TypeField`}
    />
  ),
  document_name: (
    <DocumentListViewerLink<CampaignSourceDocument>
      urlSource="document_url"
      nameSource="document_name"
      label="resources.bo_common.b2b.documents.fields.document_name"
      testId={testId}
    />
  ),
  document_url: (
    <TextField
      label="resources.bo_common.b2b.documents.fields.document_url"
      data-testid={`${testId}_DocumentUrlField`}
    />
  ),
} satisfies ColumnList<CampaignSourceDocument>;

const displayedColumns: (keyof CampaignSourceDocument)[] = ['type', 'document_name'];

const EmptyState = () => (
  <EmptyScreen
    label="resources.bo_common.b2b.campaigns.tabs.documents.empty"
    svg={<EmptyFolderSvg />}
  />
);

export const B2bCampaignDocumentsTab = () => {
  const record = useRecordContext<GetB2bCampaignOneResponse>();

  const documents = useMemo((): CampaignSourceDocument[] => {
    if (!record) {
      return [];
    }

    const rows: CampaignSourceDocument[] = [];

    if (G.isNotNullable(record.distribution_rule_file_url)) {
      rows.push({
        id: 'affiliates_data',
        type: 'affiliates_data',
        document_url: record.distribution_rule_file_url,
        document_name: getFileTitleFromUrl(record.distribution_rule_file_url),
      });
    }

    if (G.isNotNullable(record.allocation_file_url)) {
      rows.push({
        id: 'allocations',
        type: 'allocations',
        document_url: record.allocation_file_url,
        document_name: getFileTitleFromUrl(record.allocation_file_url),
      });
    }

    return rows;
  }, [record]);

  const listContext = useList({
    data: documents,
    isLoading: !record,
  });

  if (!record) {
    return null;
  }

  return (
    <ListContextProvider value={listContext}>
      <Box sx={{ paddingTop: '3px', paddingBottom: '3px' }}>
        <DatagridTyped<CampaignSourceDocument>
          columnRegistry={columns}
          displayedColumns={displayedColumns}
          sortableColumns={[]}
          bulkActionButtons={false}
          empty={<EmptyState />}
          showCard={true}
        />
      </Box>
    </ListContextProvider>
  );
};
